import { Injectable, inject, signal } from '@angular/core';
import { Company } from '../../shared/models/company-model';
import { LoggingService } from './logging.service';

export type ViewType = 'time' | 'ticker';
export type ChamberFilter = 'all' | 'House' | 'Senate';
export type GroupByType = 'member' | 'ticker';

export interface CongressFilters {
  selectedTimePeriod: number;
  selectedChamber: ChamberFilter;
  selectedGroupBy: GroupByType;
  currentView: ViewType;
  selectedCompany: Company | null;
}

@Injectable({
  providedIn: 'root'
})
export class CongressFilterService {
  private readonly STORAGE_KEY = 'congress-filters';
  private logger = inject(LoggingService);

  // Default filter values
  private readonly DEFAULT_FILTERS: CongressFilters = {
    selectedTimePeriod: 6,
    selectedChamber: 'all',
    selectedGroupBy: 'ticker',
    currentView: 'time',
    selectedCompany: null
  };

  // Signals for reactive state management
  selectedTimePeriod = signal<number>(this.DEFAULT_FILTERS.selectedTimePeriod);
  selectedChamber = signal<ChamberFilter>(this.DEFAULT_FILTERS.selectedChamber);
  selectedGroupBy = signal<GroupByType>(this.DEFAULT_FILTERS.selectedGroupBy);
  currentView = signal<ViewType>(this.DEFAULT_FILTERS.currentView);
  selectedCompany = signal<Company | null>(this.DEFAULT_FILTERS.selectedCompany);

  constructor() {
    this.loadFiltersFromStorage();
  }

  /**
   * Load filter settings from localStorage
   */
  private loadFiltersFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const filters: CongressFilters = JSON.parse(stored);

        // Update signals with stored values
        this.selectedTimePeriod.set(filters.selectedTimePeriod ?? this.DEFAULT_FILTERS.selectedTimePeriod);
        this.selectedChamber.set(filters.selectedChamber ?? this.DEFAULT_FILTERS.selectedChamber);
        this.selectedGroupBy.set(filters.selectedGroupBy ?? this.DEFAULT_FILTERS.selectedGroupBy);
        this.currentView.set(filters.currentView ?? this.DEFAULT_FILTERS.currentView);
        this.selectedCompany.set(filters.selectedCompany ?? this.DEFAULT_FILTERS.selectedCompany);
      }
    } catch (error) {
      this.logger.warn('Failed to load Congress filters from localStorage:', error);
      this.resetToDefaults();
    }
  }

  /**
   * Save current filter settings to localStorage
   */
  private saveFiltersToStorage(): void {
    try {
      const filters: CongressFilters = {
        selectedTimePeriod: this.selectedTimePeriod(),
        selectedChamber: this.selectedChamber(),
        selectedGroupBy: this.selectedGroupBy(),
        currentView: this.currentView(),
        selectedCompany: this.selectedCompany()
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      this.logger.warn('Failed to save Congress filters to localStorage:', error);
    }
  }

  /**
   * Update time period filter
   */
  updateTimePeriod(period: number): void {
    this.selectedTimePeriod.set(period);
    this.saveFiltersToStorage();
  }

  /**
   * Update chamber filter
   */
  updateChamber(chamber: ChamberFilter): void {
    this.selectedChamber.set(chamber);
    this.saveFiltersToStorage();
  }

  /**
   * Update group by filter
   */
  updateGroupBy(groupBy: GroupByType): void {
    this.selectedGroupBy.set(groupBy);
    this.saveFiltersToStorage();
  }

  /**
   * Update current view
   */
  updateView(view: ViewType): void {
    this.currentView.set(view);
    this.saveFiltersToStorage();
  }

  /**
   * Update selected company
   */
  updateCompany(company: Company | null): void {
    this.selectedCompany.set(company);
    this.saveFiltersToStorage();
  }

  /**
   * Reset all filters to default values
   */
  resetToDefaults(): void {
    this.selectedTimePeriod.set(this.DEFAULT_FILTERS.selectedTimePeriod);
    this.selectedChamber.set(this.DEFAULT_FILTERS.selectedChamber);
    this.selectedGroupBy.set(this.DEFAULT_FILTERS.selectedGroupBy);
    this.currentView.set(this.DEFAULT_FILTERS.currentView);
    this.selectedCompany.set(this.DEFAULT_FILTERS.selectedCompany);

    // Clear from localStorage
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      this.logger.warn('Failed to clear Congress filters from localStorage:', error);
    }
  }

  /**
   * Get current filter state as object
   */
  getCurrentFilters(): CongressFilters {
    return {
      selectedTimePeriod: this.selectedTimePeriod(),
      selectedChamber: this.selectedChamber(),
      selectedGroupBy: this.selectedGroupBy(),
      currentView: this.currentView(),
      selectedCompany: this.selectedCompany()
    };
  }

  /**
   * Check if filters have been modified from defaults
   */
  hasCustomFilters(): boolean {
    const current = this.getCurrentFilters();
    return (
      current.selectedTimePeriod !== this.DEFAULT_FILTERS.selectedTimePeriod ||
      current.selectedChamber !== this.DEFAULT_FILTERS.selectedChamber ||
      current.selectedGroupBy !== this.DEFAULT_FILTERS.selectedGroupBy ||
      current.currentView !== this.DEFAULT_FILTERS.currentView ||
      current.selectedCompany !== this.DEFAULT_FILTERS.selectedCompany
    );
  }
}
